import { useEffect, useState } from 'preact/hooks';

import { api, type StartInfo } from '../api/client';
import { Header } from '../components/Header';
import { NoticeItems } from '../components/NoticeItems';
import { pick, translator } from '../i18n';
import { setPageIdentity, trustHref } from '../page';

type Props = { token: string; onStarted: (resumeToken: string | null) => void };

/** Start page for a link: what the survey is, the privacy promise, optional password (SPEC §11). */
export function Start({ token, onStarted }: Props) {
  const [info, setInfo] = useState<StartInfo | null>(null);
  const [failure, setFailure] = useState<'notFound' | 'closed' | 'rateLimited' | null>(null);
  const [password, setPassword] = useState('');
  const [wrongPassword, setWrongPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [background, setBackground] = useState<Record<string, string>>({});
  // The browser's language if the survey offers it, else the primary: the server decides from `lang`.
  const [lang, setLang] = useState(navigator.language.slice(0, 2));

  useEffect(() => {
    void api.GET('/api/p/links/{token}', { params: { path: { token }, query: { lang } } }).then(({ data, response }) => {
      if (data) setInfo(data);
      else setFailure(response.status === 410 ? 'closed' : 'notFound');
    });
  }, [token, lang]);

  const t = translator(info?.locale ?? navigator.language.slice(0, 2));
  useEffect(() => {
    if (!info) return;
    document.documentElement.lang = info.locale;
    setPageIdentity(`${pick(info.title, info.locale)} – ${info.branding.displayName}`, info.branding.logo);
  }, [info]);

  if (failure) return <Message text={t(failure)} />;
  if (!info) return <Message text={t('loading')} />;
  if (info.status !== 'open') return <Message text={t('closed')} />;

  // The start page keeps it short (visual draft): what the privacy level means, the caveat about
  // free text, and who leads the conversation. The trust page, one link away, has everything.
  const level = info.notice.sections.find((s) => s.id === 'level')!;
  const ai = info.notice.sections.find((s) => s.id === 'ai')!;
  const short = [level.items[0], level.items.find((i) => 'disclosure' in i), ai.items[0]].filter((i) => i !== undefined);

  const start = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    // "Prefer not to say" is sent as null: the participant chose not to answer.
    const bg = Object.fromEntries(info.background.map((q) => [q.key, background[q.key] || null]));
    const { data, response } = await api.POST('/api/p/sessions', {
      body: { linkToken: token, locale: info.locale, ...(info.passwordRequired && { password }), ...(info.background.length && !info.hasSession && { background: bg }) },
    });
    setBusy(false);
    if (data) return onStarted(data.resumeToken);
    if (response.status === 403 || response.status === 401) setWrongPassword(true);
    else if (response.status === 429) setFailure('rateLimited');
    else setFailure(response.status === 410 ? 'closed' : 'notFound');
  };

  return (
    <div class="page" style={{ '--brand': info.branding.colours.primary, '--on-brand': info.branding.colours.onPrimary ?? '#FFFFFF' }}>
      <Header branding={info.branding} locale={info.locale} t={t} trustSlug={info.surveySlug} />
      <main class="start">
        {info.locales.length > 1 && !info.hasSession && <LanguageChoice locales={info.locales} current={info.locales.includes(lang) ? lang : info.locale} legend={t('language')} onChoose={setLang} />}
        <h1>{pick(info.title, info.locale)}</h1>
        {info.intro && <p class="lead">{pick(info.intro, info.locale)}</p>}
        <section class="notice" aria-labelledby="notice-level">
          <h2 id="notice-level" class="notice-level">
            {info.notice.level}
          </h2>
          <NoticeItems items={short} t={t} />
          <p>
            <a href={trustHref(info.surveySlug, info.locale)}>{t('trustLinkFull')}</a>
          </p>
        </section>
        <form onSubmit={start} class="start-form">
          {info.background.length > 0 && !info.hasSession && (
            <fieldset class="question">
              <legend>{t('backgroundTitle')}</legend>
              <p class="muted">{t('backgroundHelp')}</p>
              {info.background.map((q) => (
                <div class="field" key={q.key}>
                  <label for={`bg-${q.key}`}>{pick(q.labels, info.locale)}</label>
                  <select id={`bg-${q.key}`} value={background[q.key] ?? ''} onChange={(e) => setBackground({ ...background, [q.key]: (e.target as HTMLSelectElement).value })}>
                    <option value="">{t('preferNot')}</option>
                    {q.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {pick(o.labels, info.locale)}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </fieldset>
          )}
          {info.passwordRequired && (
            <div class="field">
              <label for="password">{t('password')}</label>
              <input
                id="password"
                type="password"
                autocomplete="off"
                value={password}
                aria-invalid={wrongPassword}
                aria-describedby={wrongPassword ? 'password-error' : undefined}
                onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              />
              {wrongPassword && (
                <p id="password-error" class="error" role="alert">
                  {t('passwordWrong')}
                </p>
              )}
            </div>
          )}
          <button type="submit" class="button-primary" disabled={busy}>
            {info.hasSession ? t('continue') : t('start')}
          </button>
        </form>
      </main>
    </div>
  );
}

/** A language's name in itself ("Svenska", "English"), so each reader finds their own. */
const ownName = (code: string) => {
  const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
  return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
};

/**
 * The language choice, when the survey offers more than one. Each name is in its own language and
 * marked so (WCAG 3.1.2); the choice shows at once, and the start page, notice included, reloads in that language. The
 * session keeps the language chosen when it starts.
 */
function LanguageChoice({ locales, current, legend, onChoose }: { locales: string[]; current: string; legend: string; onChoose: (l: string) => void }) {
  return (
    <fieldset class="language-choice">
      <legend>{legend}</legend>
      <div class="checks">
        {locales.map((l) => (
          <label class="check" key={l} lang={l}>
            <input type="radio" name="language" value={l} checked={current === l} onChange={() => onChoose(l)} />
            {ownName(l)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function Message({ text }: { text: string }) {
  return (
    <main class="page message-page">
      <p>{text}</p>
    </main>
  );
}
